import { BdcShipmentKind, BdcShipmentStatus } from "../types/bdc.type";
import { ApiResponse } from "../types/express-data.type";
import axios from "./axios";

/**
 * BDC · ไฟล์ส่ง 7-11 (W10) — BE PR #903 `/bdc/edi-exports`.
 *
 *   GET  /edi-exports/shipments    one day's non-draft shipments + whether each file can take them
 *   POST /edi-exports/preview      build the files, write nothing (names, counts, sha256, POs it locks)
 *   POST /edi-exports              write them (stored + history); the Invoice file's POs → รอนำส่ง 7-11
 *   GET  /edi-exports              history
 *   GET  /edi-exports/:uuid/download  the stored bytes (TIS-620, CRLF) — kept as a Blob, never decoded
 *
 * Refusals carry `error.code` + `error.po_numbers` (409 BDC_EDI_INVOICE_REQUIRES_INVOICED /
 * BDC_EDI_DO_REQUIRES_DO_CONFIRMED / BDC_EDI_SHIPMENT_CHANGED · 422 BDC_EDI_TEST_MODE /
 * BDC_EDI_ORIGINAL_MISSING / BDC_EDI_ORIGINAL_MISMATCH · 502 storage) — the screen shows them
 * itself, so the global toast is off for preview / create.
 *
 * P2: the CN file (RETSEV_*.txt) goes through the same preview / create / history / download with
 * `{ type: "cn", credit_note_uuids }` — issued CNs → queued_7_11 (they can no longer be cancelled).
 */

/** `cn` exists in the history type (phase 2); the shipment files are `do` and `invoice`. */
export type BdcEdiExportType = "do" | "invoice" | "cn";
export type BdcEdiFileType = "do" | "invoice";

/** One row of the ไฟล์ส่ง 7-11 screen. */
export interface BdcEdiShipment {
  shipment_uuid: string;
  company_id: string;
  branch_code: string | null;
  po_number: string;
  kind: BdcShipmentKind;
  status: BdcShipmentStatus;
  dc_code: string;
  dc_name: string;
  round: number | null;
  order_date: string;
  delivery_date: string;
  is_test_mode: boolean;
  store_count: number;
  delivered_pieces: number;
  invoice_no: string | null;
  grand_total: number | null;
  vat_amount: number | null;
  queued_at: string | null;
  queued_by_name: string | null;
  do_exported_at: string | null;
  invoice_exported_at: string | null;
  /** The DO file can take it now (ยืนยัน DO onward, not test mode, has stores). */
  can_export_do: boolean;
  /** The Invoice file can take it now (ออก INV แล้ว, a B… INV, not test mode) — writing it locks. */
  can_export_invoice: boolean;
}

export interface BdcEdiShipmentListParams {
  delivery_date?: string;
  order_date?: string;
  dc_code?: string;
  status?: Exclude<BdcShipmentStatus, "draft">;
  company_id?: string;
}

/** A file as it would be written (preview) — the same figures the history keeps. */
export interface BdcEdiFilePreview {
  type: BdcEdiExportType;
  file_name: string;
  /** The DESKTOP EAI profile to upload it under. */
  eai_profile: string;
  record_count: number;
  line_count: number;
  bytes: number;
  sha256: string;
  po_numbers: string[];
  invoice_no_from: string | null;
  invoice_no_to: string | null;
  /** The CN file's CN numbers (type `cn`). */
  note_nos?: string[];
}

export interface BdcEdiPreview {
  shipment_count: number;
  files: BdcEdiFilePreview[];
  /** Asked for but empty: the DO of POs without a PICKLST (kind X). */
  skipped_types: BdcEdiFileType[];
  /** The POs writing these files moves to รอนำส่ง 7-11 (the Invoice file's). */
  locks_po_numbers: string[];
  /** The CNs writing the CN file moves to queued_7_11. */
  locks_note_nos?: string[];
  credit_note_count?: number;
}

/** One written file in the history. */
export interface BdcEdiExport extends BdcEdiFilePreview {
  uuid: string;
  run_uuid: string;
  company_ids: string[];
  invoice_nos: string[];
  shipment_count: number;
  created_by_name: string | null;
  created_at: string;
}

export interface BdcEdiCreateData {
  run_uuid: string;
  rows: BdcEdiExport[];
  count: number;
  skipped_types: BdcEdiFileType[];
  /** Now รอนำส่ง 7-11; their INVs can no longer be cancelled. */
  queued_po_numbers: string[];
  /** The CN file: the CNs now queued_7_11. */
  queued_note_nos?: string[];
}

export interface BdcEdiShipmentExportRequest {
  /** 1 … 100. */
  shipment_uuids: string[];
  /** Left out = both files. */
  types?: BdcEdiFileType[];
}

/** The RETSEV file of issued CNs (1 … 100). */
export interface BdcEdiCreditNoteExportRequest {
  type: "cn";
  credit_note_uuids: string[];
}

export type BdcEdiExportRequest = BdcEdiShipmentExportRequest | BdcEdiCreditNoteExportRequest;

export interface BdcEdiExportListParams {
  type?: BdcEdiExportType;
  date_from?: string;
  date_to?: string;
  /** File name, PO or INV number. */
  search?: string;
  company_id?: string;
  page: number;
  limit: number;
}

const ownErrors = { suppressForbiddenRedirect: true, suppressErrorToast: true } as const;

export const listBdcEdiShipments = (params: BdcEdiShipmentListParams) => {
  const query = new URLSearchParams();
  if (params.delivery_date) query.append("delivery_date", params.delivery_date);
  if (params.order_date) query.append("order_date", params.order_date);
  if (params.dc_code) query.append("dc_code", params.dc_code);
  if (params.status) query.append("status", params.status);
  if (params.company_id) query.append("company_id", params.company_id);
  return axios.get<ApiResponse<{ rows: BdcEdiShipment[]; count: number }>>(
    `/bdc/edi-exports/shipments?${query.toString()}`,
  );
};

export const previewBdcEdiExport = (body: BdcEdiExportRequest) =>
  axios.post<ApiResponse<BdcEdiPreview>>(`/bdc/edi-exports/preview`, body, { meta: ownErrors });

export const createBdcEdiExport = (body: BdcEdiExportRequest) =>
  axios.post<ApiResponse<BdcEdiCreateData>>(`/bdc/edi-exports`, body, { meta: ownErrors });

export const listBdcEdiExports = (params: BdcEdiExportListParams) => {
  const query = new URLSearchParams({ page: String(params.page), limit: String(params.limit) });
  if (params.type) query.append("type", params.type);
  if (params.date_from) query.append("date_from", params.date_from);
  if (params.date_to) query.append("date_to", params.date_to);
  if (params.search) query.append("search", params.search);
  if (params.company_id) query.append("company_id", params.company_id);
  return axios.get<ApiResponse<{ rows: BdcEdiExport[]; count: number }>>(`/bdc/edi-exports?${query.toString()}`);
};

/** The stored file byte for byte — a Blob saved as is (TIS-620 must not go through a text decoder). */
export const downloadBdcEdiExport = (uuid: string) =>
  axios.get<Blob>(`/bdc/edi-exports/${encodeURIComponent(uuid)}/download`, {
    responseType: "blob",
    meta: {
      suppressForbiddenRedirect: true,
      errorMsgByStatus: {
        404: "ไม่พบไฟล์ส่ง 7-11 นี้",
        500: "ไฟล์ในที่เก็บไม่ตรงกับ checksum ที่บันทึกไว้",
        502: "อ่านไฟล์จากที่เก็บไม่สำเร็จ กรุณาลองใหม่",
      },
    },
  });
